import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-lg text-xs font-bold uppercase tracking-wide transition-all focus-visible:outline-none disabled:pointer-events-none disabled:opacity-40",
  {
    variants: {
      variant: {
        default:     "bg-[rgba(0,255,209,0.10)] text-[#00ffd1] border border-[rgba(0,255,209,0.18)] hover:bg-[rgba(0,255,209,0.18)] active:scale-[0.97]",
        destructive: "bg-[rgba(239,68,68,0.10)] text-[#f87171] border border-[rgba(239,68,68,0.2)] hover:bg-[rgba(239,68,68,0.18)] active:scale-[0.97]",
        warning:     "bg-[rgba(251,191,36,0.10)] text-[#fbbf24] border border-[rgba(251,191,36,0.2)] hover:bg-[rgba(251,191,36,0.18)] active:scale-[0.97]",
        success:     "bg-[rgba(0,255,102,0.10)] text-[#00ff66] border border-[rgba(0,255,102,0.2)] hover:bg-[rgba(0,255,102,0.18)] active:scale-[0.97]",
        info:        "bg-[rgba(56,189,248,0.10)] text-[#38bdf8] border border-[rgba(56,189,248,0.2)] hover:bg-[rgba(56,189,248,0.18)] active:scale-[0.97]",
        outline:     "bg-transparent text-[#e8f4f0] border border-[rgba(255,255,255,0.12)] hover:bg-[rgba(255,255,255,0.05)] active:scale-[0.97]",
        secondary:   "bg-[rgba(167,139,250,0.10)] text-[#a78bfa] border border-[rgba(167,139,250,0.2)] hover:bg-[rgba(167,139,250,0.18)] active:scale-[0.97]",
        ghost:       "bg-transparent text-[#4a6680] hover:bg-[rgba(255,255,255,0.05)] hover:text-[#e8f4f0]",
        muted:       "bg-[rgba(255,255,255,0.04)] text-[#4a6680] border border-[rgba(255,255,255,0.07)] hover:bg-[rgba(255,255,255,0.08)] hover:text-[#e8f4f0]",
        link:        "text-[#00ffd1] underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-4 py-2",
        inline: "h-5 px-1.5 rounded-sm text-xs tracking-wider",
        xs: "h-7 rounded-sm px-2 text-sm",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
